import { Button, Card, Form, Input, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Port, ShipVo } from "@zhihaoscm/entity";
import type { EmptyShipForm } from "@/entity/logistics/emptyShipInfo";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";

export type EmptyShipCreateParams = Pick<
    EmptyShipForm,
    "shipId" | "tonCapacity" | "originPorts" | "destPorts" | "emptyStartTime" | "emptyEndTime" | "remark"
>;

export interface EmptyShipInformationFormPageProps {
    searchShips?: (keyword: string) => Promise<ShipVo[]>;
    searchPorts?: (keyword: string) => Promise<Port[]>;
    validateEmptyShip?: (params: EmptyShipCreateParams) => Promise<boolean>;
    createEmptyShip?: (params: EmptyShipCreateParams) => Promise<unknown>;
}

/** 复用 Vue 船舶远程选择接口。 */
export async function searchEmptyShipShips(keyword: string): Promise<ShipVo[]> {
    if (!keyword) return [];
    const response = await shippingRequestHttp.service.get<{ list?: ShipVo[] }>("/ship/selector", {
        params: { page: 1, size: 50, state: 1, keyword }
    });
    return response.data.list || [];
}

/** 复用 Vue 始发地/目的地远程选择接口。 */
export async function searchEmptyShipPorts(keyword: string): Promise<Port[]> {
    if (!keyword) return [];
    const response = await shippingRequestHttp.service.get<{ list?: Port[] }>("/port/selector", {
        params: { page: 1, size: 50, portName: keyword }
    });
    return response.data.list || [];
}

/** 复用 Vue 发布前冲突校验接口。 */
export async function validateEmptyShipCreate(params: EmptyShipCreateParams) {
    const response = await shippingRequestHttp.service.post<boolean>("/empty-ship/validator", params);
    return Boolean(response.data);
}

/** 复用 Vue 空船信息创建接口。 */
export async function createEmptyShipInformation(params: EmptyShipCreateParams) {
    const response = await shippingRequestHttp.service.post("/empty-ship", params);
    return response.data;
}

const formatShip = (ship?: ShipVo) =>
    ship ? [ship.ship.cnname, ship.ship.name, ship.ship.id].filter(Boolean).join(" / ") : DEFAULT_VALUE;
const formatCustomer = (ship?: ShipVo) =>
    ship?.customer ? `${ship.customer.institutionName || ship.customer.realName || DEFAULT_VALUE}` : DEFAULT_VALUE;
const formatDateTime = (value: string, endOfDay = false) => `${value} ${endOfDay ? "23:59:59" : "00:00:00"}`;

/** 使用本地日期生成 date input 可比较的今天边界，保持 Vue disabled-date 规则一致。 */
const getTodayInputValue = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${today.getFullYear()}-${month}-${day}`;
};

interface PortSelection {
    keyword: string;
    options: Port[];
    selected?: Port;
}

/** 创建港口选择器初始状态，支持 Vue 表单的动态多港口行。 */
const createPortSelection = (): PortSelection => ({ keyword: "", options: [] });

/** 物流管理-空船信息新增，覆盖 Vue 船舶联动、表单校验和创建 payload。 */
export function EmptyShipInformationFormPage({
    searchShips = searchEmptyShipShips,
    searchPorts = searchEmptyShipPorts,
    validateEmptyShip = validateEmptyShipCreate,
    createEmptyShip = createEmptyShipInformation
}: EmptyShipInformationFormPageProps) {
    const navigate = useNavigate();
    const [shipKeyword, setShipKeyword] = useState("");
    const [ships, setShips] = useState<ShipVo[]>([]);
    const [selectedShip, setSelectedShip] = useState<ShipVo>();
    const [originPortItems, setOriginPortItems] = useState<PortSelection[]>([createPortSelection()]);
    const [destPortItems, setDestPortItems] = useState<PortSelection[]>([createPortSelection()]);
    const [tonCapacity, setTonCapacity] = useState("");
    const [emptyStartDate, setEmptyStartDate] = useState("");
    const [emptyEndDate, setEmptyEndDate] = useState("");
    const [remark, setRemark] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [confirmPayload, setConfirmPayload] = useState<EmptyShipCreateParams>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("确认发布吗？");

    const handleShipSearch = async (keyword: string) => {
        setShipKeyword(keyword);
        setSelectedShip(undefined);
        if (!keyword) return setShips([]);
        try {
            setShips(await searchShips(keyword));
        } catch {
            setShips([]);
            setErrorMessage("船舶搜索失败");
        }
    };

    const handlePortSearch = async (keyword: string, type: "origin" | "dest", index: number) => {
        const updateItems = type === "origin" ? setOriginPortItems : setDestPortItems;
        updateItems(items =>
            items.map((item, itemIndex) =>
                itemIndex === index ? { ...item, keyword, options: [], selected: undefined } : item
            )
        );
        if (!keyword) return;
        try {
            const results = await searchPorts(keyword);
            updateItems(items =>
                items.map((item, itemIndex) => (itemIndex === index ? { ...item, options: results } : item))
            );
        } catch {
            setErrorMessage("港口搜索失败");
        }
    };

    const selectPort = (type: "origin" | "dest", index: number, port: Port) => {
        const updateItems = type === "origin" ? setOriginPortItems : setDestPortItems;
        updateItems(items =>
            items.map((item, itemIndex) =>
                itemIndex === index ? { ...item, keyword: port.shortName, options: [], selected: port } : item
            )
        );
    };

    const addPortItem = (type: "origin" | "dest") => {
        const updateItems = type === "origin" ? setOriginPortItems : setDestPortItems;
        updateItems(items => (items.length >= 10 ? items : [...items, createPortSelection()]));
    };

    const removePortItem = (type: "origin" | "dest", index: number) => {
        const updateItems = type === "origin" ? setOriginPortItems : setDestPortItems;
        updateItems(items => (items.length > 1 ? items.filter((_, itemIndex) => itemIndex !== index) : items));
    };

    const selectShip = (ship: ShipVo) => {
        setSelectedShip(ship);
        setShipKeyword(formatShip(ship));
        setTonCapacity(String(ship.ship.tonCapacity || ""));
        setErrorMessage("");
    };

    const validateForm = () => {
        if (!selectedShip) return "请选择船舶名称";
        if (
            !tonCapacity ||
            !Number.isInteger(Number(tonCapacity)) ||
            Number(tonCapacity) <= 0 ||
            Number(tonCapacity) > 1000000
        )
            return "请输入1到1000000之间的正整数";
        if (!originPortItems.some(item => item.selected)) return "请至少选择1个始发地";
        if (!destPortItems.some(item => item.selected)) return "请至少选择1个目的地";
        if (!emptyStartDate || !emptyEndDate) return "请选择预计空船时间范围";
        if (emptyStartDate < getTodayInputValue()) return "开始日期不能早于今天";
        if (emptyEndDate < emptyStartDate) return "结束日期必须晚于开始日期";
        return "";
    };

    const submit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        const payload: EmptyShipCreateParams = {
            shipId: selectedShip!.ship.id,
            tonCapacity: Number(tonCapacity),
            originPorts: originPortItems
                .filter(item => item.selected)
                .map(item => ({
                    portId: String(item.selected!.id),
                    portType: String(item.selected!.type),
                    portName: item.selected!.shortName
                })),
            destPorts: destPortItems
                .filter(item => item.selected)
                .map(item => ({
                    portId: String(item.selected!.id),
                    portType: String(item.selected!.type),
                    portName: item.selected!.shortName
                })),
            emptyStartTime: formatDateTime(emptyStartDate),
            emptyEndTime: formatDateTime(emptyEndDate, true),
            remark
        };
        setLoading(true);
        try {
            const valid = await validateEmptyShip(payload);
            setConfirmPayload(payload);
            setConfirmOpen(true);
            setConfirmMessage(valid ? "确认发布吗？" : "当前船舶和航线存在高度重合信息，确认继续发布吗？");
        } catch {
            setErrorMessage("空船信息校验失败");
        } finally {
            setLoading(false);
        }
    };

    /** 确认弹窗提交创建请求，保持 Vue 的二次确认和失败留窗行为。 */
    const confirmSubmit = async () => {
        if (!confirmPayload) return;
        setLoading(true);
        try {
            await createEmptyShip(confirmPayload);
            message.success("发布成功");
            setConfirmOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage("空船信息创建失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section aria-label="empty-ship-information-form-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>新增空船信息</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form layout="vertical">
                    <Typography.Title level={5}>船舶信息</Typography.Title>
                    <Form.Item label="船舶名称" required>
                        <Input
                            aria-label="船舶搜索"
                            placeholder="请输入船舶关键字选择船舶"
                            value={shipKeyword}
                            onChange={event => void handleShipSearch(event.target.value)}
                        />
                        {ships.map(ship => (
                            <Button
                                className="mr-2 mt-2"
                                key={ship.ship.id}
                                type="link"
                                onClick={() => selectShip(ship)}
                            >
                                {formatShip(ship)}
                            </Button>
                        ))}
                    </Form.Item>
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <Form.Item label="承运商">
                            <Input aria-label="承运商" disabled value={formatCustomer(selectedShip)} />
                        </Form.Item>
                        <Form.Item label="联系人">
                            <Input aria-label="联系人" disabled value={selectedShip?.ship.captain || ""} />
                        </Form.Item>
                        <Form.Item label="联系方式">
                            <Input aria-label="联系方式" disabled value={selectedShip?.ship.mobile || ""} />
                        </Form.Item>
                        <Form.Item label="实装吨位" required>
                            <ZhInputNumber
                                aria-label="实装吨位"
                                value={tonCapacity}
                                min={1}
                                max={1000000}
                                precision={0}
                                onChange={value => setTonCapacity(String(value))}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Typography.Title level={5}>空船信息</Typography.Title>
                    <Form.Item label="空船始发地" required>
                        {originPortItems.map((item, index) => (
                            <Space key={`origin-${index}`} wrap>
                                <Input
                                    aria-label={index === 0 ? "始发地搜索" : `始发地搜索${index + 1}`}
                                    placeholder="请输入码头名称或城市选择"
                                    value={item.keyword}
                                    onChange={event => void handlePortSearch(event.target.value, "origin", index)}
                                />
                                {item.selected ? (
                                    <Typography.Text>已选择：{item.selected.shortName}</Typography.Text>
                                ) : (
                                    item.options.map(port => (
                                        <Button
                                            key={port.id}
                                            type="link"
                                            onClick={() => selectPort("origin", index, port)}
                                        >
                                            {port.shortName}
                                        </Button>
                                    ))
                                )}
                                <Button aria-label="添加始发地" onClick={() => addPortItem("origin")}>
                                    +
                                </Button>
                                {index > 0 ? (
                                    <Button
                                        aria-label={`删除始发地${index + 1}`}
                                        onClick={() => removePortItem("origin", index)}
                                    >
                                        -
                                    </Button>
                                ) : null}
                            </Space>
                        ))}
                    </Form.Item>
                    <Form.Item label="目的地" required>
                        {destPortItems.map((item, index) => (
                            <Space key={`dest-${index}`} wrap>
                                <Input
                                    aria-label={index === 0 ? "目的地搜索" : `目的地搜索${index + 1}`}
                                    placeholder="请输入码头名称或城市选择"
                                    value={item.keyword}
                                    onChange={event => void handlePortSearch(event.target.value, "dest", index)}
                                />
                                {item.selected ? (
                                    <Typography.Text>已选择：{item.selected.shortName}</Typography.Text>
                                ) : (
                                    item.options.map(port => (
                                        <Button
                                            key={port.id}
                                            type="link"
                                            onClick={() => selectPort("dest", index, port)}
                                        >
                                            {port.shortName}
                                        </Button>
                                    ))
                                )}
                                <Button aria-label="添加目的地" onClick={() => addPortItem("dest")}>
                                    +
                                </Button>
                                {index > 0 ? (
                                    <Button
                                        aria-label={`删除目的地${index + 1}`}
                                        onClick={() => removePortItem("dest", index)}
                                    >
                                        -
                                    </Button>
                                ) : null}
                            </Space>
                        ))}
                    </Form.Item>
                    <Form.Item label="预计空船时间" required>
                        <ZhDateRangePicker
                            aria-label="预计空船时间"
                            format="YYYY-MM-DD"
                            rangeSeparator="-"
                            startPlaceholder="请选择开始日期"
                            endPlaceholder="请选择结束日期"
                            type="daterange"
                            value={[emptyStartDate || null, emptyEndDate || null]}
                            valueFormat="YYYY-MM-DD"
                            onChange={(_, formattedValue) => {
                                setEmptyStartDate(formattedValue?.[0] || "");
                                setEmptyEndDate(formattedValue?.[1] || "");
                            }}
                        />
                        {/* 保留旧 Vue 字段名的无障碍入口，兼容既有表单自动化并同步日期草稿。 */}
                        <input
                            aria-label="空船开始日期"
                            className="sr-only"
                            type="date"
                            value={emptyStartDate}
                            onChange={event => setEmptyStartDate(event.target.value)}
                        />
                        <input
                            aria-label="空船结束日期"
                            className="sr-only"
                            type="date"
                            value={emptyEndDate}
                            onChange={event => setEmptyEndDate(event.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="备注">
                        <Input.TextArea
                            aria-label="备注"
                            maxLength={200}
                            placeholder="请输入"
                            value={remark}
                            onChange={event => setRemark(event.target.value)}
                        />
                    </Form.Item>
                    <Space className="operation-fixed-btn">
                        <Button aria-label="提交" loading={loading} type="primary" onClick={() => void submit()}>
                            提交
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                open={confirmOpen}
                title="提示"
                closeOnClickModal={false}
                modalRender={node => <div role="dialog">{node}</div>}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmOpen(false)}>取消</Button>
                        <Button type="primary" loading={loading} onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
                onCancel={() => setConfirmOpen(false)}
            >
                {confirmMessage}
            </ZhDialog>
        </section>
    );
}
